import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface ProtectedRouteNoLayoutProps {
  children: React.ReactNode;
  requiredPermissions?: {
    route: string;
    actions: ('view' | 'create' | 'edit' | 'delete' | 'approve')[];
  };
}

export function ProtectedRouteNoLayout({ children, requiredPermissions }: ProtectedRouteNoLayoutProps) {
  const { isAuthenticated, isLoading, userMenuPermissions } = useAuth();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check route-level permissions if specified
  if (requiredPermissions) {
    const permission = userMenuPermissions.find(p => p.route === requiredPermissions.route && p.isActive);
    
    if (!permission) {
      // User has no access to this route at all
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 text-red-500">
              <svg fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
            <p className="text-gray-600">You don't have permission to access this page.</p>
          </div>
        </div>
      );
    }

    // Check if user has at least one of the required actions
    const hasRequiredAction = requiredPermissions.actions.some(action => {
      switch (action) {
        case 'view': return permission.canView;
        case 'create': return permission.canCreate;
        case 'edit': return permission.canEdit;
        case 'delete': return permission.canDelete;
        case 'approve': return permission.canApprove;
        default: return false;
      }
    });

    if (!hasRequiredAction) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 text-red-500">
              <svg fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Insufficient Permissions</h2>
            <p className="text-gray-600">You don't have the required permissions to access this page.</p>
          </div>
        </div>
      );
    }
  }

  // Render children directly without Layout wrapper
  return <>{children}</>;
}
